import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, CreditCard, Mail, Sparkles, AlertTriangle } from "lucide-react";
import { api, mensajeError } from "../api/client";
import { PageHeader } from "../components/ui/PageHeader";
import { Spinner } from "../components/ui/Spinner";
import { Badge } from "../components/ui/Badge";

interface SuscripcionData {
  id: string;
  plan: "MENSUAL" | "ANUAL" | "DEMO";
  estado: "ACTIVA" | "DEMO" | "VENCIDA" | "SUSPENDIDA" | "CANCELADA";
  fechaInicio: string;
  fechaVencimiento: string;
  canceladaEnPeriodo: boolean;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
}

interface Pago {
  id: string;
  monto: number;
  moneda: string;
  plan: string;
  estado: string;
  fecha: string;
}

interface PrecioPlan {
  original: number;
  actual: number;
}

interface Respuesta {
  suscripcion: SuscripcionData | null;
  pagos: Pago[];
  precios: { MENSUAL: PrecioPlan; ANUAL: PrecioPlan };
  pagosConfigurados: boolean;
}

function estadoColor(estado?: string): "verde" | "morado" | "amarillo" | "rojo" | "gris" {
  if (estado === "ACTIVA") return "verde";
  if (estado === "DEMO") return "morado";
  if (estado === "VENCIDA") return "amarillo";
  if (estado === "SUSPENDIDA") return "rojo";
  return "gris";
}

function etiquetaEstado(estado?: string) {
  const mapa: Record<string, string> = {
    ACTIVA: "Activa",
    DEMO: "Demo",
    VENCIDA: "Vencida",
    SUSPENDIDA: "Suspendida",
    CANCELADA: "Cancelada",
  };
  return estado ? mapa[estado] ?? estado : "Sin suscripción";
}

export default function Suscripcion() {
  const [params] = useSearchParams();
  const [datos, setDatos] = useState<Respuesta | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [procesando, setProcesando] = useState<string | null>(null);

  async function cargar() {
    setCargando(true);
    try {
      const res = await api.get("/suscripcion");
      setDatos(res.data);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  async function pagar(plan: "MENSUAL" | "ANUAL") {
    setProcesando(plan);
    setError(null);
    try {
      const res = await api.post("/suscripcion/checkout", { plan });
      if (!res.data?.url) {
        setError("No se pudo iniciar el pago. Verifica tu conexión e intenta de nuevo.");
        setProcesando(null);
        return;
      }
      window.location.href = res.data.url;
    } catch (err) {
      setError(mensajeError(err));
      setProcesando(null);
    }
  }

  async function abrirPortal() {
    setProcesando("portal");
    setError(null);
    try {
      const res = await api.post("/suscripcion/portal");
      if (!res.data?.url) {
        setError("No se pudo abrir el portal de pagos. Verifica tu conexión e intenta de nuevo.");
        setProcesando(null);
        return;
      }
      window.location.href = res.data.url;
    } catch (err) {
      setError(mensajeError(err));
      setProcesando(null);
    }
  }

  async function cancelar() {
    if (!confirm("¿Cancelar los pagos recurrentes? Tu suscripción seguirá activa hasta el final del período ya pagado.")) return;
    setProcesando("cancelar");
    setError(null);
    try {
      await api.post("/suscripcion/cancelar");
      await cargar();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setProcesando(null);
    }
  }

  if (cargando) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  const s = datos?.suscripcion ?? null;
  const vencida = s?.estado === "VENCIDA" || s?.estado === "SUSPENDIDA";

  return (
    <div>
      <PageHeader titulo="Suscripción" descripcion="Estado de tu plan, próximo pago y opciones de contacto." icono={<CreditCard size={20} />} />

      {params.get("pago") === "exitoso" && (
        <div className="mb-6 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
          <CheckCircle2 size={16} /> Pago registrado. Puede tardar unos segundos en reflejarse aquí.
        </div>
      )}
      {params.get("pago") === "cancelado" && (
        <div className="mb-6 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 text-sm">
          <XCircle size={16} /> El pago se canceló. No se realizó ningún cargo.
        </div>
      )}
      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700">Estado actual</h3>
              <Badge color={estadoColor(s?.estado)}>{etiquetaEstado(s?.estado)}</Badge>
            </div>

            {!s ? (
              <p className="text-sm text-slate-500">
                Todavía no tienes una suscripción registrada. Elige un plan abajo para activar tu cuenta.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400 text-xs">Plan</p>
                  <p className="text-slate-700 font-medium">{s.plan}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Vigente desde</p>
                  <p className="text-slate-700 font-medium">{new Date(s.fechaInicio).toLocaleDateString("es-MX")}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">{vencida ? "Venció el" : "Próximo pago / vence el"}</p>
                  <p className="text-slate-700 font-medium">{new Date(s.fechaVencimiento).toLocaleDateString("es-MX")}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Pagos recurrentes</p>
                  <p className="text-slate-700 font-medium">
                    {s.canceladaEnPeriodo ? "Cancelados (activo hasta que venza)" : s.plan === "DEMO" ? "No aplica" : "Activos"}
                  </p>
                </div>
              </div>
            )}

            {vencida && (
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm">
                <AlertTriangle size={16} />
                Tu suscripción está {s?.estado === "SUSPENDIDA" ? "suspendida" : "vencida"}. Realiza tu pago para evitar
                interrupciones en el servicio.
              </div>
            )}
          </div>

          <div className="card p-5">
            <h3 className="mb-4 text-sm font-semibold text-slate-700">Planes</h3>
            {!datos?.pagosConfigurados && (
              <p className="mb-4 text-sm text-slate-500">
                Los pagos en línea todavía no están configurados. Contacta a soporte para activar tu suscripción.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-100 p-4">
                <p className="text-sm font-semibold text-slate-700">Mensual</p>
                <div className="mt-1 flex items-baseline gap-2">
                  {datos?.precios.MENSUAL.original && datos.precios.MENSUAL.original !== datos.precios.MENSUAL.actual && (
                    <span className="text-sm text-slate-400 line-through">
                      ${datos.precios.MENSUAL.original.toLocaleString("es-MX")}
                    </span>
                  )}
                  <p className="text-2xl font-bold text-slate-800">
                    ${datos?.precios.MENSUAL.actual.toLocaleString("es-MX")}
                    <span className="text-sm font-normal text-slate-400"> / mes</span>
                  </p>
                </div>
                <button
                  className="btn-primary w-full mt-4"
                  disabled={!datos?.pagosConfigurados || procesando === "MENSUAL"}
                  onClick={() => pagar("MENSUAL")}
                >
                  <CreditCard size={16} /> {procesando === "MENSUAL" ? "Redirigiendo..." : "Pagar mensual"}
                </button>
              </div>
              <div className="rounded-xl border border-teal-100 bg-teal-50/40 p-4">
                <p className="text-sm font-semibold text-slate-700">Anual</p>
                <div className="mt-1 flex items-baseline gap-2">
                  {datos?.precios.ANUAL.original && datos.precios.ANUAL.original !== datos.precios.ANUAL.actual && (
                    <span className="text-sm text-slate-400 line-through">
                      ${datos.precios.ANUAL.original.toLocaleString("es-MX")}
                    </span>
                  )}
                  <p className="text-2xl font-bold text-slate-800">
                    ${datos?.precios.ANUAL.actual.toLocaleString("es-MX")}
                    <span className="text-sm font-normal text-slate-400"> / año</span>
                  </p>
                </div>
                <button
                  className="btn-primary w-full mt-4"
                  disabled={!datos?.pagosConfigurados || procesando === "ANUAL"}
                  onClick={() => pagar("ANUAL")}
                >
                  <CreditCard size={16} /> {procesando === "ANUAL" ? "Redirigiendo..." : "Pagar anual"}
                </button>
              </div>
            </div>

            {!!s?.stripeCustomerId && s?.plan !== "DEMO" && (
              <div className="mt-5 flex flex-wrap gap-2">
                <button className="btn-secondary" disabled={procesando === "portal"} onClick={abrirPortal}>
                  {procesando === "portal" ? "Abriendo..." : "Método de pago y facturas"}
                </button>
                {!s?.canceladaEnPeriodo && s?.estado === "ACTIVA" && (
                  <button className="btn-secondary" disabled={procesando === "cancelar"} onClick={cancelar}>
                    {procesando === "cancelar" ? "Cancelando..." : "Cancelar pagos recurrentes"}
                  </button>
                )}
              </div>
            )}
          </div>

          {datos && datos.pagos.length > 0 && (
            <div className="card overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3 font-medium">Fecha</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium">Monto</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.pagos.map((p) => (
                    <tr key={p.id} className="border-b border-slate-50 last:border-0">
                      <td className="px-4 py-3 text-slate-500 text-xs">{new Date(p.fecha).toLocaleDateString("es-MX")}</td>
                      <td className="px-4 py-3 text-slate-700">{p.plan}</td>
                      <td className="px-4 py-3 text-slate-700">
                        ${p.monto.toLocaleString("es-MX", { minimumFractionDigits: 2 })} {p.moneda}
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={p.estado === "PAGADO" ? "verde" : "rojo"}>{p.estado}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="card p-5">
            <h3 className="mb-2 text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Sparkles size={16} /> ¿Dudas sobre tu suscripción?
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Si necesitas ayuda con tu plan, facturación o quieres solicitar una prueba, contacta directamente al equipo
              de Mascotas Vet.
            </p>
            <a href="mailto:soporte@mascotas.vet" className="btn-secondary w-full justify-center">
              <Mail size={16} /> Contactar a soporte
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
